import { useState } from 'react'
import { Check, Copy, Mail } from 'lucide-react'
import { FormDialog } from '@/components/common'
import { Button } from '@/components/ui/button'
import type { EmployeeInviteResult } from '../types'
import { inviteLinkFor } from './inviteLink'

/**
 * The link, a Copy button, and whether it was emailed.
 *
 * <p>The dialog this replaces showed the bare token under "Send this link" — there was no link,
 * and nowhere it could have led. The sentence underneath says plainly who has to send it: the
 * server, when mail is set up, or the person reading this.
 */
export function InvitationLink({
  link, sentTo, emailed,
}: { link: string; sentTo: string | null; emailed: boolean }) {
  const [copied, setCopied] = useState(false)

  return (
    <div className="space-y-2">
      <div className="flex items-stretch gap-2">
        <code className="block min-w-0 flex-1 break-all rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-2 font-mono text-xs">
          {link}
        </code>
        <Button
          type="button" variant="outline" size="sm"
          onClick={() => {
            void navigator.clipboard?.writeText(link).then(() => {
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            })
          }}
        >
          {copied
            ? <Check className="size-4" aria-hidden="true" />
            : <Copy className="size-4" aria-hidden="true" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="flex items-start gap-2 text-xs text-[var(--color-muted)]">
        <Mail className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        {emailed
          ? `Emailed to ${sentTo ?? 'them'}. You can also copy the link and send it yourself.`
          : `Email is not set up, so nothing was sent. Copy the link and send it to ${sentTo ?? 'them'} yourself.`}
      </p>
    </div>
  )
}

const OUTCOME_LABELS: Record<string, string> = {
  INVITED: 'Invited',
  NO_EMAIL: 'No email on file',
  HAS_ACCOUNT: 'Already has an account',
  EMAIL_IN_USE: 'Email already used by another account',
  FAILED: 'Could not be invited',
}

/** What a bulk invitation did, person by person — including who it could not invite and why. */
export function InviteResultsDialog({
  results, onClose,
}: { results: EmployeeInviteResult[] | null; onClose: () => void }) {
  const invited = results?.filter((result) => result.outcome === 'INVITED').length ?? 0

  return (
    <FormDialog
      open={results !== null}
      onOpenChange={(open) => { if (!open) onClose() }}
      title={invited === 1 ? 'Invitation created' : `${invited} invitations created`}
      size="lg"
      submitLabel="Done"
      onSubmit={onClose}
    >
      <ul className="space-y-4 text-sm">
        {results?.map((result) => {
          const link = inviteLinkFor(null, result.acceptUrl)
          return (
            <li key={result.employeeId} className="space-y-1">
              <p>
                <span className="font-medium text-[var(--color-heading)]">
                  {result.employeeName ?? result.email}
                </span>
                <span className="ms-2 text-xs text-[var(--color-muted)]">
                  {OUTCOME_LABELS[result.outcome] ?? result.outcome}
                </span>
              </p>
              {result.note && <p className="text-xs text-[var(--color-muted)]">{result.note}</p>}
              {result.outcome === 'INVITED' && link && (
                <InvitationLink link={link} sentTo={result.email} emailed={result.emailed} />
              )}
              {result.outcome === 'INVITED' && !link && (
                <p className="text-xs text-[var(--color-muted)]">
                  {result.emailed
                    ? `Emailed to ${result.email}.`
                    : 'Re-invite them from Administration › Users to get a link to send.'}
                </p>
              )}
            </li>
          )
        })}
      </ul>
    </FormDialog>
  )
}
